import {Message} from 'GenericTexting/models/Message';
import {AsyncPage, Empty} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {createAction} from 'Shared/utilities/actionCreator';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {constant} from 'fp-ts/lib/function';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {pipe} from 'fp-ts/lib/pipeable';
import {EmptyDateRange, DateRange} from 'Core/models/DateRange';
import {Client} from 'GenericTexting/api/api';

export enum MessagesActionTypes {
  SEARCH_MESSAGES = 'message/SEARCH_MESSAGES',
  SAVE_MESSAGE = 'message/SAVE_MESSAGE',
  SEND_MESSAGES = 'message/SEND_MESSAGES',
  RESEND_MESSAGE = 'message/RESEND_MESSAGE',
  SEND_MESSAGES_BY_CLIENT_IDENTIFIER = 'message/SEND_MESSAGES_BY_CLIENT_IDENTIFIER',
  CHANGE_SAVE_MESSAGE = 'message/CHANGE_SAVE_MESSAGE',
  CHANGE_SEND_MESSAGES = 'message/CHANGE_SEND_MESSAGES',
  CHANGE_SEND_MESSAGES_BY_CLIENT_IDENTIFIER = 'message/CHANGE_SEND_MESSAGES_BY_CLIENT_IDENTIFIER',
  CHANGE_MESSAGES = 'message/CHANGE_MESSAGES',
  CHANGE_MESSAGES_FILTER = 'message/CHANGE_MESSAGES_FILTER',
  DELETE_MESSAGE = 'message/DELETE_MESSAGE',
}

export const EmptyMessagesFilter: MessagesFilter = {
  pageSize: 0,
  phoneNumber: '',
  shortCode: '',
  messageId: '',
  message: '',
  subscriptionList: '',
  clientName: '',
  keywords: '',
  timeStamp: EmptyDateRange,
};

export type MessagesFilter = {
  pageSize?: number;
  phoneNumber: string;
  shortCode: string;
  messageId: string;
  message: string;
  subscriptionList: string;
  clientName: string;
  keywords: string;
  timeStamp: DateRange;
};

export const searchMessages = (pageOption?: Page<Message>, filter?: MessagesFilter) => {
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );
  return createAction(MessagesActionTypes.SEARCH_MESSAGES, payload);
};
export const saveMessage = (message: Message, onSuccess?: () => void) =>
  createAction(MessagesActionTypes.SAVE_MESSAGE, {message, onSuccess});
export const sendMessages = (message: Message, onSuccess?: () => void) =>
  createAction(MessagesActionTypes.SEND_MESSAGES, {message, onSuccess});
export const resendMessage = (message: Message, onSuccess?: () => void) =>
  createAction(MessagesActionTypes.RESEND_MESSAGE, {message, onSuccess});
export const sendMessagesByClient = (client: Client, onSuccess?: () => void) =>
  createAction(MessagesActionTypes.SEND_MESSAGES_BY_CLIENT_IDENTIFIER, {client, onSuccess});
export const changeSaveMessage = (message: string) => createAction(MessagesActionTypes.CHANGE_SAVE_MESSAGE, message);
export const changeSendMessages = (message: string) => createAction(MessagesActionTypes.CHANGE_SEND_MESSAGES, message);
export const changeSendMessagesByClientId = (clientId: string) =>
  createAction(MessagesActionTypes.CHANGE_SEND_MESSAGES_BY_CLIENT_IDENTIFIER, clientId);
export const changeMessagesFilter = (filters: MessagesFilter) =>
  createAction(MessagesActionTypes.CHANGE_MESSAGES_FILTER, {filters});
export const changeMessages = (subscriptions: AsyncPage<Page<Message>>) =>
  createAction(MessagesActionTypes.CHANGE_MESSAGES, subscriptions);

type MessagesActions =
  | ReturnType<typeof searchMessages>
  | ReturnType<typeof changeMessagesFilter>
  | ReturnType<typeof changeMessages>
  | ReturnType<typeof changeSaveMessage>
  | ReturnType<typeof changeSendMessages>
  | ReturnType<typeof changeSendMessagesByClientId>;

type MessageState = {
  messages: AsyncPage<Page<Message>>;
  filter: MessagesFilter;
  message: string;
  clientName: string;
};

export const DEFAULT_STATE = {
  messages: new Empty(),
  filter: EmptyMessagesFilter,
  message: '',
  clientName: '',
};

export default (state: MessageState = DEFAULT_STATE, action: MessagesActions): MessageState => {
  switch (action.type) {
    case MessagesActionTypes.CHANGE_MESSAGES_FILTER:
      return {
        ...state,
        filter: action.payload.filters,
      };
    case MessagesActionTypes.CHANGE_SAVE_MESSAGE:
      return {
        ...state,
        message: action.payload,
      };
    case MessagesActionTypes.CHANGE_SEND_MESSAGES:
      return {
        ...state,
        message: action.payload,
      };
    case MessagesActionTypes.CHANGE_SEND_MESSAGES_BY_CLIENT_IDENTIFIER:
      return {
        ...state,
        clientName: action.payload,
      };
    case MessagesActionTypes.CHANGE_MESSAGES:
      return {
        ...state,
        messages: action.payload,
      };
    default:
      return state;
  }
};
